/**
 * SettingsPage.jsx — components/shared/SettingsPage.jsx
 *
 * Three sections:
 *   1. AI Model — pick Haiku vs Sonnet (admin only; guests see read-only)
 *   2. Accounts — admin can add/edit/remove guest accounts
 *   3. Watchlist — drag-to-reorder, bulk edit, remove, add
 */

import { useState, useEffect, useCallback } from 'react';
import {
  isAdmin, getUsername, getRole, getLastLoginAt,
  listAccounts, createAccount, updateAccount, deleteAccount,
  MODEL_OPTIONS, getModelPref, setModelPref,
  settingsGet, settingsPut,
} from '../../lib/api';
import { fetchCacheStatus, invalidateCache } from '../earnings/EarningsShared';

// ── Shared UI helpers ─────────────────────────────────────────────────────────
const inputStyle = (focus) => ({
  width: '100%', padding: '7px 10px', borderRadius: 5, boxSizing: 'border-box',
  border: `1px solid ${focus ? 'var(--accent)' : 'var(--border)'}`,
  background: 'var(--bg)', color: 'var(--text)', fontSize: 12,
  fontFamily: 'monospace', outline: 'none',
});

function SectionCard({ title, icon, children }) {
  return (
    <div style={{ background: 'var(--bg-elev)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', marginBottom: 16 }}>
      <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', background: 'var(--bg)', display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 14 }}>{icon}</span>
        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', textTransform: 'uppercase', color: 'var(--muted)' }}>{title}</span>
      </div>
      <div style={{ padding: 16 }}>{children}</div>
    </div>
  );
}

function StatusMsg({ type, msg }) {
  if (!msg) return null;
  return (
    <div style={{
      padding: '7px 12px', borderRadius: 5, fontSize: 11, marginTop: 8,
      background: type === 'ok' ? 'var(--up-soft)' : 'var(--down-soft)',
      color: type === 'ok' ? 'var(--up)' : 'var(--down)',
      border: `1px solid ${type === 'ok' ? 'var(--up)' : 'var(--down)'}`,
    }}>{type === 'ok' ? '✓ ' : '⚠ '}{msg}</div>
  );
}

// ── 1. Model Picker ───────────────────────────────────────────────────────────
function ModelPicker({ modelPref, onChange }) {
  const admin = isAdmin();
  return (
    <SectionCard title="AI Model" icon="🤖">
      <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12, lineHeight: 1.6 }}>
        Applies to Earnings, Vol Analysis, and Prospects syncs.
        {!admin && <span style={{ color: 'var(--faint)' }}> Only the admin can change this.</span>}
      </p>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        {MODEL_OPTIONS.map(opt => {
          const selected = modelPref === opt.key;
          return (
            <button
              key={opt.key}
              onClick={() => admin && onChange(opt.key)}
              disabled={!admin}
              style={{
                flex: 1, minWidth: 140, padding: '12px 16px', borderRadius: 7, cursor: admin ? 'pointer' : 'not-allowed',
                border: `2px solid ${selected ? 'var(--accent)' : 'var(--border)'}`,
                background: selected ? 'var(--accent-soft)' : 'var(--bg)',
                transition: 'all .15s', textAlign: 'left',
                opacity: !admin ? 0.6 : 1,
              }}
            >
              <div style={{ fontFamily: 'monospace', fontWeight: 700, fontSize: 13, color: selected ? 'var(--accent)' : 'var(--text)', marginBottom: 4 }}>
                {selected && '✓ '}{opt.label}
              </div>
              <div style={{ fontSize: 11, color: 'var(--muted)' }}>{opt.note}</div>
              {opt.key === 'haiku' && <div style={{ fontSize: 10, color: 'var(--faint)', marginTop: 4 }}>Recommended default</div>}
              {opt.key === 'sonnet' && <div style={{ fontSize: 10, color: 'var(--faint)', marginTop: 4 }}>Better date recall · 4× cost</div>}
            </button>
          );
        })}
      </div>
      {admin && (
        <div style={{ fontSize: 11, color: 'var(--faint)', marginTop: 10, lineHeight: 1.5 }}>
          Hybrid tip: use Haiku when the earnings calendar loads from Yahoo (it grounds the AI).
          Switch to Sonnet only if Yahoo is unavailable and you need better recall of report dates.
        </div>
      )}
    </SectionCard>
  );
}

// ── 2. Account Management (admin only) ───────────────────────────────────────
function AccountManager() {
  const [accounts, setAccounts] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [newUser,  setNewUser]  = useState('');
  const [newPass,  setNewPass]  = useState('');
  const [creating, setCreating] = useState(false);
  const [status,   setStatus]   = useState(null); // { type: 'ok'|'err', msg }
  const [editing,  setEditing]  = useState(null); // { username, newUsername, newPassword }
  const [focus,    setFocus]    = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await listAccounts();
      setAccounts(d.accounts || []);
    } catch(e) { setStatus({ type: 'err', msg: e.message }); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (isAdmin()) load(); }, [load]);

  const create = async () => {
    if (!newUser.trim() || !newPass.trim()) return;
    setCreating(true); setStatus(null);
    try {
      await createAccount(newUser.trim(), newPass.trim());
      setNewUser(''); setNewPass('');
      setStatus({ type: 'ok', msg: `Guest "${newUser.trim()}" created.` });
      load();
    } catch(e) { setStatus({ type: 'err', msg: e.message }); }
    finally { setCreating(false); }
  };

  const remove = async (username) => {
    if (!confirm(`Remove account "${username}"?`)) return;
    setStatus(null);
    try {
      await deleteAccount(username);
      setStatus({ type: 'ok', msg: `"${username}" removed.` });
      load();
    } catch(e) { setStatus({ type: 'err', msg: e.message }); }
  };

  const saveEdit = async () => {
    if (!editing) return;
    setStatus(null);
    try {
      await updateAccount(editing.username, editing.newUsername || undefined, editing.newPassword || undefined);
      setStatus({ type: 'ok', msg: `"${editing.username}" updated.` });
      setEditing(null);
      load();
    } catch(e) { setStatus({ type: 'err', msg: e.message }); }
  };

  if (!isAdmin()) return null;

  return (
    <SectionCard title="Guest Accounts" icon="👥">
      {loading ? (
        <div style={{ color: 'var(--faint)', fontSize: 12 }}>Loading…</div>
      ) : (
        <>
          {/* Account list */}
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 16 }}>
            <thead>
              <tr>
                {['Username', 'Role', 'Created', 'Actions'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '6px 8px', fontSize: 10, color: 'var(--muted)', letterSpacing: '.08em', textTransform: 'uppercase', borderBottom: '1px solid var(--border)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {accounts.map(a => (
                <tr key={a.username}>
                  {editing?.username === a.username ? (
                    // Inline edit row
                    <td colSpan={4} style={{ padding: '8px', borderBottom: '1px solid var(--border)' }}>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        <input
                          style={{ ...inputStyle(focus.eu), flex: 1, minWidth: 100 }}
                          value={editing.newUsername}
                          onChange={e => setEditing(ed => ({ ...ed, newUsername: e.target.value }))}
                          onFocus={() => setFocus(f => ({ ...f, eu: true }))}
                          onBlur={() => setFocus(f => ({ ...f, eu: false }))}
                          placeholder="New username"
                        />
                        <input
                          type="password"
                          style={{ ...inputStyle(focus.ep), flex: 1, minWidth: 100 }}
                          value={editing.newPassword}
                          onChange={e => setEditing(ed => ({ ...ed, newPassword: e.target.value }))}
                          onFocus={() => setFocus(f => ({ ...f, ep: true }))}
                          onBlur={() => setFocus(f => ({ ...f, ep: false }))}
                          placeholder="New password (leave blank to keep)"
                        />
                        <button onClick={saveEdit} style={btnStyle('var(--up)')}>Save</button>
                        <button onClick={() => setEditing(null)} style={btnStyle('var(--faint)')}>Cancel</button>
                      </div>
                    </td>
                  ) : (
                    <>
                      <td style={{ padding: '8px', borderBottom: '1px solid var(--border)', fontFamily: 'monospace', fontSize: 12 }}>
                        {a.username}
                        {a.username === getUsername() && <span style={{ marginLeft: 6, fontSize: 9, color: 'var(--accent)', background: 'var(--accent-soft)', padding: '1px 5px', borderRadius: 3 }}>you</span>}
                      </td>
                      <td style={{ padding: '8px', borderBottom: '1px solid var(--border)', fontSize: 11, color: a.role === 'admin' ? 'var(--warn)' : 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.08em' }}>{a.role}</td>
                      <td style={{ padding: '8px', borderBottom: '1px solid var(--border)', fontSize: 11, color: 'var(--faint)', fontFamily: 'monospace' }}>{a.createdAt?.split('T')[0] || '—'}</td>
                      <td style={{ padding: '8px', borderBottom: '1px solid var(--border)' }}>
                        {a.role !== 'admin' && (
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button
                              onClick={() => setEditing({ username: a.username, newUsername: a.username, newPassword: '' })}
                              style={btnStyle('var(--accent)')}
                            >Edit</button>
                            <button onClick={() => remove(a.username)} style={btnStyle('var(--down)')}>Remove</button>
                          </div>
                        )}
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>

          {/* Add new guest */}
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 8 }}>Add Guest Account</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <input
              value={newUser} onChange={e => setNewUser(e.target.value)}
              placeholder="Username"
              style={{ ...inputStyle(focus.nu), flex: 1, minWidth: 120 }}
              onFocus={() => setFocus(f => ({ ...f, nu: true }))}
              onBlur={() => setFocus(f => ({ ...f, nu: false }))}
            />
            <input
              type="password" value={newPass} onChange={e => setNewPass(e.target.value)}
              placeholder="Password" onKeyDown={e => e.key === 'Enter' && create()}
              style={{ ...inputStyle(focus.np), flex: 1, minWidth: 120 }}
              onFocus={() => setFocus(f => ({ ...f, np: true }))}
              onBlur={() => setFocus(f => ({ ...f, np: false }))}
            />
            <button onClick={create} disabled={creating || !newUser || !newPass} style={btnStyle('var(--accent)')}>
              {creating ? 'Adding…' : '+ Add'}
            </button>
          </div>
          <StatusMsg {...(status || {})} msg={status?.msg} />
        </>
      )}
    </SectionCard>
  );
}

// ── 3. Watchlist Manager ──────────────────────────────────────────────────────
// ── Shared button styles ──────────────────────────────────────────────────────
const btnStyle = (color) => ({
  background: 'none', border: `1px solid ${color}`, color,
  padding: '5px 12px', borderRadius: 5, fontSize: 11, fontWeight: 700,
  cursor: 'pointer', whiteSpace: 'nowrap', letterSpacing: '.04em',
});

// ── 4. Cache Management (admin only) ─────────────────────────────────────────
function CacheManager() {
  const [status,  setStatus]   = useState(null);
  const [loading, setLoading]  = useState(false);
  const [msg,     setMsg]      = useState(null);

  const load = async () => {
    setLoading(true);
    try { setStatus(await fetchCacheStatus()); } catch(e) { setMsg({ type: 'err', msg: e.message }); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (isAdmin()) load(); }, []);

  const bust = async (keys) => {
    setMsg(null);
    try {
      await invalidateCache(keys);
      setMsg({ type: 'ok', msg: `Cache cleared: ${keys.join(', ')}` });
      load();
    } catch(e) { setMsg({ type: 'err', msg: e.message }); }
  };

  if (!isAdmin()) return null;

  const entries = status ? Object.entries(status.status) : [];

  return (
    <SectionCard title="Shared KV Cache" icon="☁">
      <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12, lineHeight: 1.6 }}>
        Earnings, Vol Analysis, and Prospects responses are cached in Cloudflare KV and shared across all users.
        First sync of the session calls Haiku; all others get the cached result instantly at no cost.
      </p>

      {loading && <div style={{ fontSize: 12, color: 'var(--faint)' }}>Loading cache status…</div>}

      {entries.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 12 }}>
          <thead>
            <tr>
              {['Type', 'Status', 'Age', 'Actions'].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '5px 8px', fontSize: 10, color: 'var(--muted)', letterSpacing: '.08em', textTransform: 'uppercase', borderBottom: '1px solid var(--border)' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {entries.map(([key, val]) => (
              <tr key={key}>
                <td style={{ padding: '7px 8px', borderBottom: '1px solid var(--border)', fontFamily: 'monospace', fontSize: 12, textTransform: 'uppercase', letterSpacing: '.06em' }}>{key}</td>
                <td style={{ padding: '7px 8px', borderBottom: '1px solid var(--border)' }}>
                  {val.cached
                    ? <span style={{ fontSize: 10, background: 'var(--up-soft)', color: 'var(--up)', padding: '1px 7px', borderRadius: 3, fontWeight: 700 }}>CACHED</span>
                    : <span style={{ fontSize: 10, background: 'var(--border)', color: 'var(--faint)', padding: '1px 7px', borderRadius: 3 }}>EMPTY</span>
                  }
                </td>
                <td style={{ padding: '7px 8px', borderBottom: '1px solid var(--border)', fontSize: 11, fontFamily: 'monospace', color: 'var(--muted)' }}>
                  {val.cached ? val.age : '—'}
                </td>
                <td style={{ padding: '7px 8px', borderBottom: '1px solid var(--border)' }}>
                  {val.cached && (
                    <button onClick={() => bust([key])} style={btnStyle('var(--down)')}>Clear</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button onClick={() => bust(['earnings','volanalysis','prospects'])} style={btnStyle('var(--down)')}>Clear All Caches</button>
        <button onClick={load} disabled={loading} style={btnStyle('var(--accent)')}>↺ Refresh Status</button>
      </div>
      <StatusMsg {...(msg || {})} msg={msg?.msg} />
    </SectionCard>
  );
}

// ── 5. Session Plan Settings ──────────────────────────────────────────────────
function SessionPlanSettings({ value, onChange }) {
  const [draft,   setDraft]   = useState(value || '');
  const [focus,   setFocus]   = useState(false);
  const [status,  setStatus]  = useState(null);
  const [saving,  setSaving]  = useState(false);

  // Sync if parent updates (e.g. loaded from server)
  useEffect(() => { setDraft(value || ''); }, [value]);

  const save = async () => {
    setSaving(true); setStatus(null);
    try {
      await settingsPut({ sessionPlanPlaceholder: draft.trim() });
      onChange(draft.trim());
      setStatus({ type: 'ok', msg: 'Placeholder saved.' });
      setTimeout(() => setStatus(null), 2500);
    } catch(e) {
      setStatus({ type: 'err', msg: e.message });
    } finally { setSaving(false); }
  };

  const clear = async () => {
    setDraft('');
    setSaving(true);
    try {
      await settingsPut({ sessionPlanPlaceholder: '' });
      onChange('');
      setStatus({ type: 'ok', msg: 'Placeholder cleared.' });
      setTimeout(() => setStatus(null), 2500);
    } catch(e) {
      setStatus({ type: 'err', msg: e.message });
    } finally { setSaving(false); }
  };

  return (
    <SectionCard title="Session Plan" icon="📋">
      <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12, lineHeight: 1.6 }}>
        Set the placeholder text shown in the session plan editor when no note has been entered yet.
        Use it as a daily reminder of your checklist, process, or rules.
      </p>
      <textarea
        value={draft}
        onChange={e => setDraft(e.target.value)}
        placeholder={'e.g. Bias? Key levels? Max loss? Entry rules? Exit rules?'}
        rows={5}
        style={{
          ...inputStyle(focus),
          resize: 'vertical', lineHeight: 1.65,
          fontFamily: 'monospace', fontSize: 12,
        }}
        onFocus={() => setFocus(true)}
        onBlur={() => setFocus(false)}
      />
      <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
        <button onClick={save} disabled={saving} style={btnStyle('var(--accent)')}>
          {saving ? 'Saving…' : '✓ Save Placeholder'}
        </button>
        {draft && (
          <button onClick={clear} disabled={saving} style={btnStyle('var(--muted)')}>
            Clear
          </button>
        )}
      </div>
      <StatusMsg {...(status || {})} msg={status?.msg} />
    </SectionCard>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────
export default function SettingsPage({ watchlist: _watchlist, onUpdateWatchlist: _onUpdateWatchlist, onSettingsChange }) {
  const [modelPref,           setModel]          = useState(getModelPref);
  const [sessionPlaceholder,  setPlaceholder]    = useState('');

  // Load server settings on mount
  useEffect(() => {
    settingsGet()
      .then(d => {
        if (d.sessionPlanPlaceholder != null) setPlaceholder(d.sessionPlanPlaceholder);
      })
      .catch(() => {});
  }, []);

  const changeModel = (m) => {
    setModelPref(m);
    setModel(m);
  };

  const handlePlaceholderChange = (val) => {
    setPlaceholder(val);
    onSettingsChange?.({ sessionPlanPlaceholder: val });
  };

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: '16px 16px 60px' }}>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 11, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 4 }}>
          Settings
        </div>
        <div style={{ fontSize: 12, color: 'var(--faint)', display: 'flex', flexWrap: 'wrap', gap: '4px 12px', alignItems: 'center' }}>
          <span>
            Logged in as <span style={{ fontFamily: 'monospace', color: 'var(--text)' }}>{getUsername()}</span>
            {' '}·{' '}
            <span style={{ color: getRole() === 'admin' ? 'var(--warn)' : 'var(--muted)', textTransform: 'uppercase', fontSize: 10 }}>{getRole()}</span>
          </span>
          {getLastLoginAt() && (
            <span style={{ color: 'var(--faint)', fontSize: 11 }}>
              · Last login{' '}
              <span style={{ fontFamily: 'monospace', color: 'var(--muted)' }}>
                {new Date(getLastLoginAt()).toLocaleString('en-US', {
                  month: 'short', day: 'numeric', year: 'numeric',
                  hour: '2-digit', minute: '2-digit', hour12: false,
                })}
              </span>
            </span>
          )}
        </div>
      </div>

      <ModelPicker modelPref={modelPref} onChange={changeModel} />
      <SessionPlanSettings value={sessionPlaceholder} onChange={handlePlaceholderChange} />
      <AccountManager />
      <CacheManager />
    </div>
  );
}
